/**
 * The "something changed" bus.
 *
 * Every successful write in the app — a save, an approval, an upload, a
 * delete, a punch, a stock issue — is announced here, and every mounted screen
 * refetches. That is why no page carries a refresh button: the data carries
 * the news of its own change instead of waiting for a timer.
 *
 * Two transports, because "anywhere" means two different things:
 *
 *   same tab       a window CustomEvent, so every mounted screen in this tab
 *                  hears it, including the one that made the change — it
 *                  should show the server's answer, not its optimistic one;
 *   other tabs     BroadcastChannel where the browser has it, falling back to
 *                  a localStorage ping where it does not. Two tabs on one
 *                  machine, or the owner and the manager on one desktop, both
 *                  update the moment either writes.
 *
 * What it deliberately does NOT cover: another machine entirely. No browser
 * event can see a save made on a different computer, which is exactly what
 * useAutoRefresh's interval timer remains for.
 *
 * Every call is wrapped — this is a convenience layer, and a browser without
 * BroadcastChannel, or a storage event blocked by policy, must degrade to
 * "the screens still poll" rather than break the write that triggered it.
 */

const EVENT_NAME = "lss:data-changed";
const CHANNEL_NAME = "lss-live";
const FALLBACK_KEY = "lss-live-ping";

let channel = null;
try {
  if (typeof BroadcastChannel !== "undefined") channel = new BroadcastChannel(CHANNEL_NAME);
} catch (_) {
  channel = null;
}

/** Announce that the server's data just changed. Never throws. */
export function notifyChange() {
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  } catch (_) {
    /* no window (SSR/tests) — the poll still covers us */
  }
  if (channel) {
    try {
      channel.postMessage(Date.now());
      return; // delivered to every other tab; no need for the fallback too
    } catch (_) {
      channel = null; // channel died (document torn down) — fall through
    }
  }
  try {
    localStorage.setItem(FALLBACK_KEY, String(Date.now()));
  } catch (_) {
    /* private mode / quota — polling covers it */
  }
}

/**
 * Listen for changes from anywhere. Returns an unsubscribe function.
 *
 * The callback is invoked once per change regardless of transport, and is
 * never given any payload — a subscriber only ever needs to know that it
 * should refetch, never what changed. Deciding that a particular screen can
 * skip a refetch belongs to that screen's own guard, not here.
 */
export function subscribe(fn) {
  const onLocal = () => fn();
  window.addEventListener(EVENT_NAME, onLocal);

  const onChannel = () => fn();
  if (channel) channel.addEventListener("message", onChannel);

  // Fallback transport. Note that `storage` fires only in OTHER tabs, which is
  // precisely the gap the CustomEvent above does not cover.
  const onStorage = (e) => {
    if (e.key === FALLBACK_KEY) fn();
  };
  window.addEventListener("storage", onStorage);

  return () => {
    window.removeEventListener(EVENT_NAME, onLocal);
    window.removeEventListener("storage", onStorage);
    if (channel) channel.removeEventListener("message", onChannel);
  };
}
